/**
 * Typed endpoint wrappers — one function per API route.
 *
 * Keep these thin: validate nothing, shape nothing. Just call api.* and
 * return the typed response. Business logic belongs in React Query hooks
 * (hooks/useMe, hooks/useBets, etc.).
 */
import { api } from './client';
import type { paths } from '@/types/openapi.generated';
import type {
  MonthlyFeeAccrual,
  MeResponse,
  Bet,
  Deposit,
  Withdrawal,
  Market,
  AuthTokens,
  ResponsibleGamblingLimits,
  ResponsibleGamblingLimitsPut,
} from '@/types/api';
import type { AllocatePayIdBody, PayIdDepositState } from '@/features/deposits/payid/types';

// --- Auth ---
export const authApi = {
  signup: (body: {
    email: string;
    password: string;
    firstName: string;
    phantomWinAcknowledged: true;
    referralCode?: string;
  }) => api.post<AuthTokens>('/auth/signup', body),

  login: (body: { email: string; password: string }) =>
    api.post<AuthTokens>('/auth/login', body),

  // Real Google sign-in: exchange a Google ID token for our session.
  // Requires GOOGLE_CLIENT_ID configured on the API (else it 403s).
  google: (body: { credential: string }) =>
    api.post<AuthTokens>('/auth/google', body),

  refresh: (body: { refreshToken: string }) =>
    api.post<AuthTokens>('/auth/refresh', body),

  logout: () => api.post<void>('/auth/logout'),

  forgotPassword: (body: { email: string }) =>
    api.post<void>('/auth/password/forgot', body),

  resetPassword: (body: { token: string; newPassword: string }) =>
    api.post<void>('/auth/password/reset', body),

  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.post<void>('/auth/password/change', body),
};

// --- Me ---
export const meApi = {
  getMe: () => api.get<MeResponse>('/me'),

  /* Derived from the contract (0051 adds capCents, feeHeadroomCents,
     billingMonthTz) — a hand-typed shape here is how the cap went unnoticed. */
  getMonthlyFee: () => api.get<MonthlyFeeAccrual>('/me/monthly-fee'),
};

// --- Sessions (one row per refresh-token family ≈ one per device) ---
export const sessionsApi = {
  list: () => api.get<{ items: SessionRow[] }>('/me/sessions'),

  /* Signing out EVERY device includes this one, so the caller must clear the
     local token store afterwards — the access token stays valid until it
     expires, and a UI that kept using it would show a session the user was
     just told had been revoked. */
  revokeAll: () => api.delete<void>('/me/sessions'),

  revokeOne: (familyId: string) => api.delete<void>(`/me/sessions/${familyId}`),
};

export interface SessionRow {
  familyId: string;
  sessionStartedAt: string;
  lastActivityAt: string;
  issuedIp?: string | null;
  issuedUserAgent?: string | null;
}

// --- Notification preferences ---
/* PUT takes a PARTIAL and the server merges it, so a toggle sends only the one
   channel/category it changed. Sending the whole object back would let a stale
   read silently revert another tab's change. `security` is force-true
   server-side whatever is submitted. */
export type NotificationCategory =
  | 'monthly_fee_notice'
  | 'withdrawal_updates'
  | 'engagement'
  | 'marketing'
  | 'security';

export type NotificationChannel = 'email' | 'push';

export type NotificationPrefs = Record<
  NotificationChannel,
  Partial<Record<NotificationCategory, boolean>>
>;

export const notificationsApi = {
  get: () => api.get<{ preferences: NotificationPrefs }>('/me/notifications/preferences'),

  patch: (body: Partial<NotificationPrefs>) =>
    api.put<{ preferences: NotificationPrefs }>('/me/notifications/preferences', body),
};

// --- Bets ---
export const betsApi = {
  list: (params?: { limit?: number; status?: Bet['status'] }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.size ? `?${qs.toString()}` : '';
    return api.get<{ items: Bet[]; nextCursor: string | null }>(`/bets${suffix}`);
  },

  /* quoteId is REQUIRED by the wire contract (migration 0036 — the server owns
     the price). It is declared here, not merely passed at the call site: a
     spread into an object literal escapes excess-property checking, so an
     under-declared body type typechecks while silently dropping the one field
     whose absence is a 422 quote_missing. */
  place: (body: {
    marketId: string;
    selectionId: string;
    quoteId: string;
    decimalOdds: number;
    stakeCents: number;
    clientBetUuid: string;
  }) => api.post<Bet>('/bets', body),   // v0.11 — all bets draw from Play Balance (server default source='balance')

  /* Straight multi (migration 0049). 2–10 legs on independent fixtures; the
     server prices the ticket from its persisted quotes (the quoteId is the
     price token, as for a single) and refuses a repeated fixture with
     multi_same_fixture. One stake, one fee, one clientBetUuid for the ticket.
     The body is the contract's MultiBetRequest, derived from the generated
     paths so a contract change fails here, not on the wire. */
  placeMulti: (body: PlaceMultiBody) => api.post<Bet>('/bets', body),
};

export type PlaceMultiBody = Extract<
  paths['/bets']['post']['requestBody']['content']['application/json'],
  { legs: unknown }
>;

// --- Deposits ---
export const depositsApi = {
  create: (body: {
    amountCents: number;
    lockTermMonths: 3 | 6 | 9 | 12;
    paymentMethodToken: string;
    /** v0.11 — first-deposit only: cents to lock into Savings; the rest is
     *  Play Balance. Omit on subsequent deposits (they go 100% to Balance). */
    savingsCents?: number;
  }) => api.post<Deposit>('/deposits', body),
};

// --- PayID deposit rail (Zepto inbound; provisional contract — see
// features/deposits/payid/types.ts) ---
export const payIdApi = {
  /** Current allocation + credits received to it. 404-free: null allocation when none. */
  get: () => api.get<PayIdDepositState>('/deposits/payid'),
  /** Allocate (or return the existing) PayID; first-deposit split rides along. */
  allocate: (body: AllocatePayIdBody) => api.post<PayIdDepositState>('/deposits/payid', body),
};

// --- KYC (pre-deposit identity verification) ---
// The default provider (mock) auto-verifies, matching the prototype's UX, so a
// user can self-verify and then deposit. Idempotent — an already-verified user
// gets alreadyVerified:true.
export const kycApi = {
  /* Body and response are the contract's (0052). Syntrico needs lastName and
     phone (E.164) on first use and no document; the mock provider still takes
     documentType. Sending all of them lets one call serve either provider —
     the server ignores what its provider doesn't need. */
  initiate: (body: KycInitiateBody) => api.post<KycInitiateResult>('/me/kyc/initiate', body),
};

export type KycInitiateBody = NonNullable<
  paths['/me/kyc/initiate']['post']['requestBody']
>['content']['application/json'];
export type KycInitiateResult =
  paths['/me/kyc/initiate']['post']['responses'][200]['content']['application/json'];

// --- Withdrawals ---
export const withdrawalsApi = {
  create: (body: { amountCents: number; destinationToken: string }) =>
    api.post<Withdrawal>('/withdrawals', body),

  cancel: (id: string) => api.post<void>(`/withdrawals/${id}/cancel`),
};

// --- Responsible-gambling limits (migration 0043) ---
// Every write returns the full envelope, so callers replace the cache with the
// response rather than re-reading. There is deliberately NO endpoint to shorten
// or revoke a cool-off or self-exclusion — the API has none, and a control a
// customer can undo at 2am is not a control.
export const limitsApi = {
  get: () => api.get<ResponsibleGamblingLimits>('/me/responsible-gambling-limits'),

  put: (body: ResponsibleGamblingLimitsPut) =>
    api.put<ResponsibleGamblingLimits>('/me/responsible-gambling-limits', body),

  coolOff: (body: { durationDays: 1 | 7 | 30 }) =>
    api.post<ResponsibleGamblingLimits>('/me/responsible-gambling-limits/cool-off', body),

  selfExclude: (body: { durationDays: number | 'permanent' }) =>
    api.post<ResponsibleGamblingLimits>('/me/responsible-gambling-limits/self-exclusion', body),
};

// --- Markets ---
export const marketsApi = {
  // Contract (openapi.yaml GET /markets): a BARE array of Market — not
  // an object wrapper. The old `{ markets: Market[] }` typing made
  // useMarkets select `data.markets` === undefined, so the UI showed
  // "No markets available" even with a healthy odds engine (2026-06-11).
  list: () => api.get<Market[]>('/markets'),
};

// --- Referrals ---
export const referralsApi = {
  getMyReferrals: () =>
    api.get<{
      referralCode: string;
      referrals: Array<{ firstName: string; status: string }>;
      totalRewardCents: number;
      capRemaining: number;
    }>('/me/referrals'),
};
